import { ApolloError } from "@apollo/client";
import { ArrowBack } from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { GqlObservationStatus } from "~/__generated__";
import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import GaiaHookFormConfirmCloseDialog from "~/modules/app/components/GaiaHookFormConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { MapPanelHeader, MapPanelScrollableArea } from "~/modules/map/components/MapPanel";
import Feedback from "~/modules/project/components/Feedback";
import { Shared_FormTemplateFragment } from "~/shared/components/FormTemplate/fragments/fragments.generated";
import RenderIf from "~/shared/components/RenderIf";

import MapEditorFeatureDetailTitle from "../MapEditorFeatureDetailTitle";
import ObservationStatus from "../ObservationStatus";
import { MapEditorObservationPanelEcologicalFunctionFragment } from "./components/EcologicalFunctionCard/fragments.generated";
import LinkableEcologicalFunctions from "./components/LinkableEcologicalFunctions";
import { LinkedEcologicalFunctions } from "./components/LinkedEcologicalFunctions/LinkedEcologicalFunctions";
import { MapEditorObservationForm } from "./components/MapEditorObservationForm";
import { ObservationActionsBox } from "./components/ObservationActionsBox";
import { ObservationCloneButton } from "./components/ObservationCloneButton";
import { MapEditorObservationPanelObservationMediaPointFragment } from "./components/ObservationMediaPointCard/fragments.generated";
import { ValidationStatusAlert } from "./components/ValidationStatusAlert";
import {
    MapEditorObservationPanelEcologicalFunctionCreatedFragment,
    MapEditorObservationPanelObservationFragment,
    MapEditorObservationPanelResearchTypeOptionFragment,
} from "./fragments.generated";
import { useChangeObservationStatusMutation } from "./useChangeObservationStatusMutation";
import { useCreateEcologicalFunctionMutation } from "./useCreateEcologicalFunctionMutation";
import { useDeleteObservationMutation } from "./useDeleteObservationMutation";
import { useLinkObservationToEcologicalFunctionMutation } from "./useLinkObservationToEcologicalFunctionMutation";
import { useSuspenseObservationEditableQuery } from "./useSuspenseObservationEditableQuery";
import { useUnlinkObservationFromEcologicalFunctionMutation } from "./useUnlinkObservationFromEcologicalFunctionMutation";
import { ObservationUpdateFormValues, useUpdateObservationMutation } from "./useUpdateObservationMutation";
import { useValidationStatus } from "./useValidationStatus";

export interface MapEditObservationPanelEditableProps {
    backHref: string;
    createHrefToEcologicalFunction: (element: MapEditorObservationPanelEcologicalFunctionFragment) => string;
    createHrefToObservationMediaPointCard: (element: MapEditorObservationPanelObservationMediaPointFragment) => string;
    mapEditorFeatureId: string;
    onEcologicalFunctionCreated?: (value: MapEditorObservationPanelEcologicalFunctionCreatedFragment) => void;
    onObservationCloned?: (clonedObservation: MapEditorObservationPanelObservationFragment) => void;
    onObservationDeleted?: () => Promise<void> | void;
    title: string;
}

export function MapEditorObservationPanelEditable({
    backHref,
    createHrefToEcologicalFunction,
    createHrefToObservationMediaPointCard,
    mapEditorFeatureId,
    onEcologicalFunctionCreated,
    onObservationCloned,
    onObservationDeleted,
    title,
}: MapEditObservationPanelEditableProps) {
    const { data } = useSuspenseObservationEditableQuery(mapEditorFeatureId);
    const observation = data.mapEditorFeatureGetById.data as MapEditorObservationPanelObservationFragment;
    const researchTypes = data.settingsResearchTypeGetAll?.data ?? [];
    const navigate = useNavigate();

    const [updateObservationAction, { error }] = useUpdateObservationMutation();
    const [createEcologicalFunctionAction] = useCreateEcologicalFunctionMutation();
    const [linkToEcologicalFunctionAction] = useLinkObservationToEcologicalFunctionMutation();
    const [unlinkObservationAction] = useUnlinkObservationFromEcologicalFunctionMutation();
    const [changeStatusAction, { loading: isChangingStatus }] = useChangeObservationStatusMutation();
    const [deleteObservationAction, { loading: isDeleting }] = useDeleteObservationMutation();

    const handleChangeStatusClick = (status: GqlObservationStatus) => {
        changeStatusAction(observation.id, status);
    };

    const handleDeleteObservationClick = async () => {
        await deleteObservationAction(observation.feature.id);
        await onObservationDeleted?.();
        navigate(backHref, { relative: "path" });
    };

    const isEcologicalFunctionLinkingAllowed =
        observation.status?.data === GqlObservationStatus.Approved ||
        observation.status?.data === GqlObservationStatus.AwaitingApproval;

    return (
        <MapEditorObservationPanelEditableImpl
            backHref={backHref}
            createHrefToEcologicalFunction={createHrefToEcologicalFunction}
            createHrefToObservationMediaPointCard={createHrefToObservationMediaPointCard}
            ecologicalFunctionsLinkable={data.observationEcologicalFunctionGetAllLinkable}
            locationFormTemplate={data.settingsFormTemplateGetMasterForm}
            isDeleting={isDeleting || isChangingStatus}
            isEcologicalFunctionLinkingAllowed={isEcologicalFunctionLinkingAllowed}
            observation={observation}
            onChangeStatus={handleChangeStatusClick}
            onCloneObservation={onObservationCloned}
            onDeleteObservation={handleDeleteObservationClick}
            researchTypes={researchTypes}
            onSubmit={values => updateObservationAction(observation.id, values)}
            onCreateEcologicalFunctionClick={async ecologicalFunctionTypeId => {
                const result = await createEcologicalFunctionAction(observation, ecologicalFunctionTypeId);
                const ecologicalFunction = result.data?.ecologicalFunctionCreate;

                if (ecologicalFunction) {
                    onEcologicalFunctionCreated?.(ecologicalFunction);
                }
            }}
            onLinkEcologicalFunctionClick={ecologicalFunctionId =>
                linkToEcologicalFunctionAction(observation.id, ecologicalFunctionId)
            }
            onUnlinkEcologicalFunctionClick={ecologicalFunctionId =>
                unlinkObservationAction(observation.id, ecologicalFunctionId)
            }
            submitError={error}
            title={title}
        />
    );
}

interface MapEditObservationPanelEditablePropsImpl {
    backHref: string;
    createHrefToEcologicalFunction: (element: MapEditorObservationPanelEcologicalFunctionFragment) => string;
    createHrefToObservationMediaPointCard: (element: MapEditorObservationPanelObservationMediaPointFragment) => string;
    ecologicalFunctionsLinkable: MapEditorObservationPanelEcologicalFunctionFragment[];
    isDeleting: boolean;
    isEcologicalFunctionLinkingAllowed: boolean;
    locationFormTemplate: Shared_FormTemplateFragment;
    observation: MapEditorObservationPanelObservationFragment;
    onChangeStatus: (status: GqlObservationStatus) => void;
    onCloneObservation?: (clonedObservation: MapEditorObservationPanelObservationFragment) => void;
    onDeleteObservation: () => Promise<unknown>;
    onSubmit: (values: ObservationUpdateFormValues) => void;
    onCreateEcologicalFunctionClick: (ecologicalFunctionTypeId: string) => void;
    onLinkEcologicalFunctionClick: (ecologicalFunctionId: string) => void;
    onUnlinkEcologicalFunctionClick: (ecologicalFunctionId: string) => void;
    researchTypes: MapEditorObservationPanelResearchTypeOptionFragment[];
    submitError?: ApolloError;
    title: string;
}

function MapEditorObservationPanelEditableImpl({
    backHref,
    createHrefToEcologicalFunction,
    createHrefToObservationMediaPointCard,
    ecologicalFunctionsLinkable,
    isDeleting,
    isEcologicalFunctionLinkingAllowed,
    locationFormTemplate,
    observation,
    onChangeStatus,
    onCloneObservation,
    onDeleteObservation,
    onSubmit,
    onCreateEcologicalFunctionClick,
    onLinkEcologicalFunctionClick,
    onUnlinkEcologicalFunctionClick,
    researchTypes,
    submitError,
    title,
}: MapEditObservationPanelEditablePropsImpl) {
    const { t } = useTranslation("mapEditor");

    // TODO: this query can be added to the parent query once we update it to use `observationId`
    // instead of `mapEditorFeatureId`
    const validationStatusQuery = useValidationStatus(observation.id);

    const { control, handleSubmit, reset, setError, setValue, watch } = useForm<ObservationUpdateFormValues>({
        disabled: false,
        values: mapObservationToFormValues(observation),
    });

    const handleSubmitHof = handleSubmit(onSubmit);
    useApplyErrorToForm(submitError, setError);

    return (
        <>
            <MapPanelHeader>
                <IconButton component={Link} color="inherit" to={backHref} relative="path">
                    <ArrowBack />
                </IconButton>
                <Typography variant="h6" flex={1}>
                    {title}
                </Typography>
                <ObservationCloneButton observationId={observation.id} onCloned={onCloneObservation} disabled={isDeleting} />
            </MapPanelHeader>
            <Box component="form" flex={1} minHeight={0} display="flex" flexDirection="column" onSubmit={handleSubmitHof}>
                <GaiaHookFormConfirmCloseDialog control={control} />

                <MapPanelScrollableArea>
                    <Stack gap={2} padding={2}>
                        {validationStatusQuery.status?.errors && validationStatusQuery.status.errors.length > 0 && (
                            <ValidationStatusAlert errors={validationStatusQuery.status.errors} />
                        )}
                        <MapEditorFeatureDetailTitle
                            title={observation.researchType.name}
                            user={observation.createdByUser}
                            titleSlot={
                                <RenderIf payload={observation.status} render={value => <ObservationStatus status={value} />} />
                            }
                        />
                        <MapEditorObservationForm
                            control={control}
                            createHrefToObservationMediaPointCard={createHrefToObservationMediaPointCard}
                            locationFormTemplate={locationFormTemplate}
                            observation={observation}
                            researchTypes={researchTypes}
                            setValue={setValue}
                            watch={watch}
                        />
                        <Feedback
                            control={control}
                            labelComment={t("featureDetailObservation.labelFeedbackComment")}
                            labelTitle={t("featureDetailObservation.labelFeedbackRating")}
                            feedbackRating={watch("feedbackRating")}
                        />
                        <Stack direction="row" gap={1} alignItems="center">
                            <GaiaDeleteButton
                                label={t("MapEditorFeatureDetails.featureDelete.btnDelete")}
                                isLoading={isDeleting}
                                onDelete={onDeleteObservation}
                            />
                        </Stack>
                        {observation.ecologicalFunctions.length > 0 && (
                            <LinkedEcologicalFunctions
                                createTo={createHrefToEcologicalFunction}
                                elements={observation.ecologicalFunctions}
                                onUnlinkClick={onUnlinkEcologicalFunctionClick}
                            />
                        )}
                        {isEcologicalFunctionLinkingAllowed && (
                            <LinkableEcologicalFunctions
                                createTo={createHrefToEcologicalFunction}
                                ecologicalFunctionsLinkable={ecologicalFunctionsLinkable}
                                onLinkClick={onLinkEcologicalFunctionClick}
                                ecologicalFunctionTypes={observation.type.ecologicalFunctionTypes}
                                onCreateFunction={onCreateEcologicalFunctionClick}
                            />
                        )}
                    </Stack>
                </MapPanelScrollableArea>

                <RenderIf
                    payload={observation.status}
                    render={currentStatus => (
                        <ObservationActionsBox
                            control={control}
                            onCancelClick={() => reset()}
                            onChangeStatusClick={onChangeStatus}
                            onSubmitClick={handleSubmitHof}
                            status={currentStatus}
                        />
                    )}
                />
            </Box>
        </>
    );
}

function mapObservationToFormValues(observation: MapEditorObservationPanelObservationFragment): ObservationUpdateFormValues {
    return {
        answers: observation.answers ?? {},
        createdAt: parseStringToDate(observation.createdAt),
        feedbackComment: observation.feedbackComment?.data,
        feedbackRating: observation.feedbackRating?.data,
        medias: observation.medias,
        observationTypeId: observation.observationTypeId,
        preciseLocation: observation.preciseLocation ?? {},
        speciesId: observation.speciesId,
    };
}
